import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseApiClient } from '@app/core/services/base-api-client.service';
import { LoadingIndicatorService } from '@app/core/services/loading-indicator.service';
import { LogService } from '@app/core/services/log.service';
import { Environment } from '@env/environment';
import { Observable, catchError, tap } from 'rxjs';
import { AuditProgress } from '../models/audit-progress';
import { QuestionCollectionResponse } from '../models/audit-collection-response';
import { QuestionCollectionRequest } from '../models/audit-collection-request';
import { InsertAnswers } from '../models/insert-answer';

const httpOptions = {
	headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};
export interface IAuditsService {
	url: string;
	getProgress(scheduledAuditId: number): Observable<AuditProgress>;
	fetchQuestionCollection(request: QuestionCollectionRequest): Observable<QuestionCollectionResponse>;
	getPerformAuditId(): Observable<number>;
}
@Injectable({
	providedIn: 'root',
})
export class AuditsService extends BaseApiClient implements IAuditsService {
	/**
	 * The base URL for the API domain.
	 */
	public baseUrl: string;

	/**
	 * The URL to the API endpoint.
	 */
	public url: string;

	constructor(
		private readonly http: HttpClient,
		private readonly log: LogService,
		private readonly loadingIndicatorService: LoadingIndicatorService,
		environment: Environment
	) {
		super();
		this.baseUrl = environment.services['employee'];
		this.url = this.baseUrl + 'audits';
	}

	/**
	 * Get Audit Progress
	 * @param scheduledAuditId
	 */
	getProgress(scheduledAuditId: number): Observable<AuditProgress> {
		const url = `${this.url}/Progress?scheduledAuditId=${scheduledAuditId}`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());

		this.loadingIndicatorService.startLoading();
		return this.http.get<AuditProgress>(url, httpOptions).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}

	/**
	 * Get PerformAuditId
	 */
	getPerformAuditId(): Observable<number> {
		const url = `${this.url}/PerformAuditId`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());

		this.loadingIndicatorService.startLoading();
		return this.http.get<number>(url, httpOptions).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}

	/**
	 * Fetch Question Collection
	 * @param request
	 */
	fetchQuestionCollection(request: QuestionCollectionRequest): Observable<QuestionCollectionResponse> {
		const url = `${this.url}/QuestionCollection`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());

		this.loadingIndicatorService.startLoading();
		return this.http.post<QuestionCollectionResponse>(url, request, httpOptions).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}

	/**
	 * Fetch Question Collection
	 * @param request
	 */
	insertAnswer(request: InsertAnswers): Observable<boolean> {
		const url = `${this.url}/Answers`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());

		this.loadingIndicatorService.startLoading();
		return this.http.post<boolean>(url, request, httpOptions).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}

	/**
	 * Get Audit Status
	 * @param scheduledAuditId
	 */
	getStatus(scheduledAuditId: number): Observable<boolean> {
		const url = `${this.url}/Status?scheduledAuditId=${scheduledAuditId}`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());

		this.loadingIndicatorService.startLoading();
		return this.http.get<boolean>(url, httpOptions).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}

	/**
	 * Submit Audit
	 * @param request
	 */
	submit(scheduledAuditId: number): Observable<boolean> {
		const url = `${this.url}/Submit?scheduledAuditId=${scheduledAuditId}`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());

		this.loadingIndicatorService.startLoading();
		return this.http.post<boolean>(url, null, httpOptions).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}
}
